import { Component, OnInit } from '@angular/core';
import { Capacitor } from '@capacitor/core';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { ToastController } from '@ionic/angular';
import { BehaviorSubject } from 'rxjs';
import { environment } from 'src/environments/environment';
import { AuthService } from '../auth.service';

const INVALID_CREDENTIAL_CODES = new Set([
  'auth/invalid-credential',
  'auth/invalid-email',
  'auth/invalid-login-credentials',
  'auth/user-not-found',
  'auth/wrong-password',
]);

@Component({
  selector: 'app-login',
  templateUrl: './login.component.html',
  styleUrls: ['./login.component.scss'],
})
export class LoginComponent implements OnInit {
  loginForm: FormGroup;
  type = 'password';
  iconName = 'eye';

  private errorLoginMsg = new BehaviorSubject<string | null>('');
  private loadSpinner = new BehaviorSubject(false);
  loadSpinner$ = this.loadSpinner.asObservable();

  constructor(
    private fb: FormBuilder,
    private router: Router,
    private authService: AuthService,
    private toastController: ToastController
  ) {}

  ngOnInit() {
    this.loginForm = this.fb.group({
      email: [null, [Validators.required, Validators.email]],
      password: [null, [Validators.required, Validators.minLength(6)]],
      rememberMe: [false],
    });
  }

  async login() {
    this.loadSpinner.next(true);
    this.errorLoginMsg.next(null);
    this.loginForm.markAsPristine();
    try {
      await this.authService.signIn(this.loginForm.value);
      this.router.navigateByUrl('/mood', { replaceUrl: true });
    } catch (error) {
      const authError = this.normalizeAuthError(error);
      this.logAuthFailure(authError);

      if (this.isCredentialError(authError.code)) {
        this.createToaster(
          `Invalid credentials. New here? <span class='message-span'>Sign up</span> now!`
        );
      } else {
        this.createToaster(
          `We couldn't sign you in right now. Please try again.`
        );
      }
    } finally {
      this.loadSpinner.next(false);
    }
  }

  changeType() {
    this.type = this.type === 'password' ? 'text' : 'password';
    this.iconName = this.iconName === 'eye' ? 'eye-off' : 'eye';
  }

  private async createToaster(message: string) {
    const t = await this.toastController.create({
      message,
      color: 'danger',
      duration: 5000,
      position: 'top',
    });
    const messageSpan = t.shadowRoot?.querySelector(
      '.message-span'
    ) as HTMLElement | null;
    if (messageSpan) {
      messageSpan.style.cursor = 'pointer';
      messageSpan.style.textDecoration = 'underLine';
      messageSpan.addEventListener('click', () => {
        this.router.navigateByUrl('/auth/sign-up');
        t.dismiss();
      });
    }
    t.present();
  }

  private isCredentialError(code: string | null) {
    return !!code && INVALID_CREDENTIAL_CODES.has(code);
  }

  private normalizeAuthError(error: unknown) {
    if (typeof error === 'string') {
      return {
        code: error,
        message: error,
      };
    }

    if (error && typeof error === 'object') {
      const firebaseError = error as { code?: unknown; message?: unknown };
      return {
        code: typeof firebaseError.code === 'string' ? firebaseError.code : null,
        message:
          typeof firebaseError.message === 'string'
            ? firebaseError.message
            : null,
      };
    }

    return {
      code: null,
      message: null,
    };
  }

  private logAuthFailure(authError: { code: string | null; message: string | null }) {
    if (environment.production) {
      return;
    }

    console.info('[auth-login]', {
      platform: Capacitor.getPlatform(),
      code: authError.code,
      message: authError.message,
      firebaseProjectId: environment.firebaseConfig.projectId,
    });
  }
}
